/**
 * MCP Bearer-Token Admin (P4.5b)
 *
 * Lists outstanding tokens; mints new ones with scoped skill access.
 * The token *secret* is shown once on issue — the operator must copy it
 * before closing the modal because the backend stores only the public
 * `token_id` after this view unmounts.
 */

import { useEffect, useState } from "react";
import { apiv2 } from "../../../apiv2";
import type { MCPTokenView } from "../../../apiv2";

interface Props {
  onBack: () => void;
}

export function MCPTokensAdmin({ onBack }: Props) {
  const [tokens, setTokens] = useState<MCPTokenView[]>([]);
  const [knownSkills, setKnownSkills] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [showIssue, setShowIssue] = useState(false);
  const [issueConsumer, setIssueConsumer] = useState("");
  const [issueScopes, setIssueScopes] = useState<Set<string>>(new Set());
  const [issuedSecret, setIssuedSecret] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const [tokenList, skills] = await Promise.all([
        apiv2.listMCPTokens(),
        apiv2.listMCPKnownSkills(),
      ]);
      setTokens(tokenList);
      setKnownSkills(skills.skills);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function handleIssue() {
    if (!issueConsumer.trim()) {
      setError("consumer_id is required");
      return;
    }
    if (issueScopes.size === 0) {
      setError("at least one scope is required");
      return;
    }
    setError(null);
    try {
      const resp = await apiv2.issueMCPToken({
        consumer_id: issueConsumer.trim(),
        scopes: Array.from(issueScopes),
      });
      setIssuedSecret(resp.secret);
      setIssueConsumer("");
      setIssueScopes(new Set());
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  async function handleRevoke(tokenId: string) {
    if (!confirm(`Revoke token ${tokenId}? This cannot be undone.`)) return;
    try {
      await apiv2.revokeMCPToken(tokenId);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  function toggleScope(scope: string) {
    const next = new Set(issueScopes);
    if (next.has(scope)) next.delete(scope);
    else next.add(scope);
    setIssueScopes(next);
  }

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold">MCP Bearer Tokens</h1>
          <p className="text-sm text-white/50 mt-1">
            Scoped credentials for MCP consumers (Claude Code, Bedrock,
            Snowflake Cortex, …)
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowIssue(true)}
            className="px-3 py-2 bg-brand-500 hover:bg-brand-400 text-white text-sm rounded"
          >
            Issue token
          </button>
          <button
            onClick={onBack}
            className="px-3 py-2 bg-white/10 hover:bg-white/15 text-sm rounded"
          >
            Back
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 text-red-300 text-sm rounded">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-white/40 text-sm">Loading…</div>
      ) : tokens.length === 0 ? (
        <div className="text-white/40 text-sm">
          No tokens issued yet. Click <em>Issue token</em> to mint the first
          one.
        </div>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-white/40 border-b border-white/10">
              <th className="py-2 pr-4">Token ID</th>
              <th className="py-2 pr-4">Consumer</th>
              <th className="py-2 pr-4">Scopes</th>
              <th className="py-2 pr-4">Status</th>
              <th className="py-2"></th>
            </tr>
          </thead>
          <tbody>
            {tokens.map((t) => (
              <tr key={t.token_id} className="border-b border-white/5">
                <td className="py-2 pr-4 font-mono text-xs">{t.token_id}</td>
                <td className="py-2 pr-4">{t.consumer_id}</td>
                <td className="py-2 pr-4 text-xs text-white/60">
                  {t.scopes.join(", ")}
                </td>
                <td className="py-2 pr-4">
                  {t.revoked ? (
                    <span className="text-red-400">revoked</span>
                  ) : (
                    <span className="text-emerald-400">active</span>
                  )}
                </td>
                <td className="py-2 text-right">
                  {!t.revoked && (
                    <button
                      onClick={() => handleRevoke(t.token_id)}
                      className="text-xs text-red-300 hover:text-red-200"
                    >
                      Revoke
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Issue modal */}
      {showIssue && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className="bg-zinc-900 border border-white/10 rounded-lg p-6 max-w-md w-full">
            <h2 className="text-lg font-semibold mb-4">Issue new token</h2>
            <label className="block text-xs text-white/60 mb-1">
              Consumer ID
            </label>
            <input
              value={issueConsumer}
              onChange={(e) => setIssueConsumer(e.target.value)}
              placeholder="e.g. snowflake-cortex"
              className="w-full px-2 py-1.5 bg-white/5 border border-white/10 rounded text-sm mb-4"
            />
            <label className="block text-xs text-white/60 mb-1">Scopes</label>
            <div className="max-h-48 overflow-y-auto border border-white/10 rounded p-2 mb-4">
              {knownSkills.length === 0 ? (
                <div className="text-xs text-white/40">
                  No skills registered yet — wire skills into the MCP server
                  first.
                </div>
              ) : (
                knownSkills.map((s) => (
                  <label
                    key={s}
                    className="flex items-center gap-2 text-xs py-1 cursor-pointer hover:bg-white/5 px-1 rounded"
                  >
                    <input
                      type="checkbox"
                      checked={issueScopes.has(s)}
                      onChange={() => toggleScope(s)}
                    />
                    <span className="font-mono">{s}</span>
                  </label>
                ))
              )}
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowIssue(false);
                  setIssueScopes(new Set());
                  setIssueConsumer("");
                }}
                className="px-3 py-1.5 bg-white/10 hover:bg-white/15 text-sm rounded"
              >
                Cancel
              </button>
              <button
                onClick={handleIssue}
                className="px-3 py-1.5 bg-brand-500 hover:bg-brand-400 text-white text-sm rounded"
              >
                Issue
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Secret-displayed-once modal */}
      {issuedSecret && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className="bg-zinc-900 border border-amber-500/40 rounded-lg p-6 max-w-md w-full">
            <h2 className="text-lg font-semibold mb-2 text-amber-300">
              Token secret (shown once)
            </h2>
            <p className="text-xs text-white/60 mb-4">
              Copy this now. It will not be displayed again; if lost, revoke and
              reissue.
            </p>
            <pre className="bg-black/40 p-3 rounded text-xs font-mono break-all border border-white/10 mb-4">
              {issuedSecret}
            </pre>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  navigator.clipboard.writeText(issuedSecret).catch(() => {});
                }}
                className="px-3 py-1.5 bg-white/10 hover:bg-white/15 text-sm rounded"
              >
                Copy
              </button>
              <button
                onClick={() => {
                  setIssuedSecret(null);
                  setShowIssue(false);
                }}
                className="px-3 py-1.5 bg-brand-500 hover:bg-brand-400 text-white text-sm rounded"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
